import {
  Box,
  Flex,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { BsArrowReturnLeft } from "react-icons/bs";
import { HiMicrophone, HiOutlineMicrophone, HiOutlineX } from "react-icons/hi";
import { HiOutlineSparkles } from "react-icons/hi2";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  Avatar,
  Button,
  ExpandableTextArea,
  IconButton,
  Link,
  PrettyMarkdown,
} from "../../../../components";
import { CopyIcon } from "../../../../components/Icons/CopyIcon";
import { copy } from "../../../../utils/clipboard";
import { CATEGORIES, useSendGAEvent } from "../../../../utils/googleAnalytics";
import { useIsExtension } from "../../../hooks/useAppEnvironmentContext";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { AskChatRole } from "../../Ask/useAskChat";
import type { AskChat, AskChatChartSpec } from "../../Ask/useAskChatJson";
import useAskChatJson from "../../Ask/useAskChatJson";
import MonospacedText from "../../MonospacedText";
import AskHeader from "./AskHeader";
import { useSpeechRecognition } from "./useSpeechRecognition";

export const suggestedCandidateMessages = [
  "Do they have offers from other companies?",
  "What compensation terms have been discussed?",
  "Summarize the candidate's last role.",
];

// TBD final interview quick actions
export const suggestedInterviewMessages = [
  "Do they have offers from other companies?",
  "What compensation terms have been discussed?",
];

export const introMessageForCandidates =
  "Ask me anything about the candidate and I'll answer using all interviews you have access to.";

export const introMessageForInterviews =
  "Hello! I'm here to help you quickly action this interview. For example, you can:\n• Ask me questions about the interview\n• Draft a follow-up email to the candidate\n• Provide a short executive summary\n\nAsk and I'll do my best to help!";

/**
 * Main chat component for candidates and interviews. Responds with cited
 * notes from the candidate's interview(s).
 */
export type AskPanelChatHookResult = {
  chat: AskChat;
  addUserMessage: (content: string, userContent: string) => Promise<void>;
  busy: boolean;
  reset: () => void;
};

export const AskPanel: React.FC<{
  requestPath: string;
  args?: { [key: string]: string | null };
  onClose?: () => void;
  hidden?: boolean;
  suggestedMessages: string[];
  introMessage: string;
  onClickTimestamp?: (timestamp: number) => void;
  eventCategory: CATEGORIES;
  inline?: boolean;
  /** When provided, use this instead of useAskChatJson (e.g. for insights chat). */
  chatHookResult?: AskPanelChatHookResult;
  /** When true, render assistant messages as markdown (e.g. insights chat). */
  renderMarkdown?: boolean;
}> = ({
  requestPath,
  args = {},
  onClose,
  hidden = false,
  suggestedMessages,
  introMessage,
  onClickTimestamp,
  eventCategory,
  inline = false,
  chatHookResult,
  renderMarkdown = false,
}) => {
  const [userInput, setUserInput] = useState("");
  const [delayedHidden, setDelayedHidden] = useState(hidden);
  const citationMap = useRef(new Map<string, number>());
  const isExtension = useIsExtension();
  const sendGAEvent = useSendGAEvent();
  const toast = useToast();

  const defaultChat = useAskChatJson(requestPath, args);
  const {
    chat,
    addUserMessage,
    busy: chatBusy,
    reset,
  } = chatHookResult ?? defaultChat;

  const { isListening, toggleListening } = useSpeechRecognition({
    onTranscript: (transcript) =>
      setUserInput((prev) => (prev ? `${prev} ${transcript}` : transcript)),
    onError: () =>
      toast({
        title: "Speech recognition not supported",
        description: "Please use Chrome or Safari for voice input.",
        status: "error",
      }),
    onPermissionDenied: () =>
      toast({
        title: "Microphone access denied",
        description: "Please allow microphone access to use voice input.",
        status: "error",
      }),
  });

  const fullReset = (): void => {
    reset();
    setUserInput("");
    citationMap.current.clear();
  };

  // Delay hiding the component to allow the exit animation to play
  useEffect(() => {
    if (hidden && !delayedHidden) {
      setTimeout(() => {
        setDelayedHidden(hidden);
      }, 200);
    } else {
      setDelayedHidden(hidden);
    }
  }, [hidden, delayedHidden]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (): void => {
    if (!userInput.trim()) return;
    addUserMessage(userInput, userInput);
    setUserInput("");
  };

  useEffect(() => {
    const scroll = scrollRef.current;
    if (scroll) {
      scroll.scrollTop = scroll.scrollHeight;
    }
  }, [chat.messages[chat.messages.length - 1]]);

  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (ref.current && !chatBusy) {
      ref.current.focus();
    }
  }, [ref.current, chatBusy, delayedHidden]);

  const onTextAreaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!userInput.trim()) return;
      sendGAEvent("ask_submit", eventCategory);
      handleSubmit();
    }
  };
  // Hide to block tabbing and save on render time
  if (delayedHidden) {
    return null;
  }

  return (
    <Flex
      flexDir="column"
      height="100%"
      minH="0"
      width="100%"
      pt={inline ? 0 : 4}
      pb={inline ? 1 : 6}
      px={inline ? 0 : 6}
      borderLeft={inline || isExtension ? undefined : "1px solid"}
      borderColor={inline || isExtension ? undefined : "gray.200"}
      boxShadow={
        inline || isExtension ? undefined : "7px 7px 28px 0px #0000001F"
      }
      overflow="hidden"
    >
      {!inline && (
        <Flex
          flexDirection="row"
          pt={2}
          pb={3}
          mb={4}
          borderBottomColor="gray.200"
          borderBottomWidth="1px"
          alignItems="center"
          flexShrink={0}
        >
          <AskHeader />
          <IconButton
            variant="icon"
            aria-label="Close"
            size="sm"
            ml="auto"
            mr="0px"
            icon={<HiOutlineX strokeWidth="1.5px" size={20} />}
            onClick={() => {
              onClose?.();
            }}
          />
        </Flex>
      )}

      <Box
        ref={scrollRef}
        overflowY="auto"
        overflowX="hidden"
        flex="1"
        minH="0"
      >
        <Flex
          flexDir="column"
          minH="100%"
          justifyContent={inline ? "flex-end" : "flex-start"}
        >
          <BotMessage
            key="static-message"
            text={introMessage}
            citationMap={citationMap.current}
            eventCategory={eventCategory}
            hideCopyButton={inline}
          />
          {inline && !chatBusy && chat.messages.length < 1 && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Awaiting instructions...
            </Text>
          )}
          {chat.messages.map((message, i) => {
            const isLatest = i === chat.messages.length - 1;
            if (message.role === AskChatRole.Assistant) {
              return (
                <BotMessage
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  text={message.userContent}
                  citationMap={citationMap.current}
                  onClickTimestamp={onClickTimestamp}
                  eventCategory={eventCategory}
                  isLatest={isLatest}
                  renderMarkdown={renderMarkdown}
                  chartSpecs={message.chartSpecs}
                />
              );
            }
            return (
              <UserMessage
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                text={message.userContent}
                isLatest={isLatest}
              />
            );
          })}
        </Flex>
      </Box>
      <Flex
        flexDir="column"
        borderTop={inline ? "none" : "1px solid"}
        borderColor="gray.200"
        pt={inline ? 2 : 4}
        mt={inline ? 5 : 4}
        flexShrink={0}
      >
        {inline && !chatBusy && chat.messages.length < 1 && (
          <Box h="1px" bg="gray.200" mt={1} mb={5} />
        )}
        {!chatBusy && chat.messages.length < 1 && (
          <Flex flexDir="column" gap={2} mb={4}>
            {suggestedMessages.map((suggestion) => (
              <SuggestedMessage
                key={suggestion}
                message={suggestion}
                onClick={() => {
                  addUserMessage(suggestion, suggestion);
                  setUserInput("");
                }}
                eventCategory={eventCategory}
              />
            ))}
          </Flex>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!userInput.trim()) return;
            sendGAEvent("ask_submit", eventCategory);
            handleSubmit();
          }}
        >
          <Box
            p="1px"
            borderRadius="md"
            background="linear-gradient(90deg, #6366F1, #A855F7)"
          >
            <ExpandableTextArea
              ref={ref}
              fontSize="sm"
              resize="none"
              onChange={(e) => setUserInput(e.target.value)}
              disabled={chatBusy}
              placeholder={inline ? "How may I help?" : "Your question...."}
              value={userInput}
              onKeyDown={onTextAreaKeyDown}
              maxHeight="110px"
              border="none"
              borderRadius="md"
              bg="white"
              _focus={{
                boxShadow: "none",
              }}
              _disabled={{
                bg: "white",
                opacity: 1,
                cursor: "not-allowed",
              }}
            />
          </Box>
          {inline ? (
            <Flex mt={2} alignItems="center" justifyContent="space-between">
              <Text fontSize="xs" color="gray.400">
                Enter to submit
              </Text>
              <Flex alignItems="center" gap={2}>
                <IconButton
                  aria-label={isListening ? "Stop listening" : "Voice input"}
                  icon={
                    <Icon
                      as={isListening ? HiMicrophone : HiOutlineMicrophone}
                      boxSize={5}
                    />
                  }
                  variant="ghost"
                  size="sm"
                  color={isListening ? "red.500" : "#4D37DE"}
                  minW="auto"
                  h="auto"
                  p={1}
                  onClick={toggleListening}
                  disabled={chatBusy}
                />
                <IconButton
                  aria-label="Submit"
                  icon={<Icon as={BsArrowReturnLeft} boxSize={5} />}
                  variant="ghost"
                  size="sm"
                  color="#4D37DE"
                  minW="auto"
                  h="auto"
                  p={1}
                  type="submit"
                  disabled={chatBusy || !userInput.trim()}
                />
              </Flex>
            </Flex>
          ) : (
            <Flex mt={1} justifyContent="space-between">
              <Button
                size="xs"
                onClick={() => {
                  sendGAEvent("ask_start_over", eventCategory);
                  setUserInput("");
                  fullReset();
                  if (ref.current) {
                    ref.current.focus();
                  }
                }}
                variant="link"
                disabled={chatBusy || chat.messages.length < 1}
                _disabled={{
                  color: "gray.200",
                  background: "none",
                }}
                _hover={{
                  textDecoration: "none",
                }}
                visibility={chat.messages.length < 1 ? "hidden" : "visible"}
              >
                Start over
              </Button>
              <Link />
              <Text fontSize="xs" color="gray.500" fontWeight="500" mt="1px">
                Press enter to submit
              </Text>
            </Flex>
          )}
        </form>
      </Flex>
    </Flex>
  );
};

const BotMessage: React.FC<{
  text?: string;
  citationMap: Map<string, number>;
  onClickTimestamp?: (timestamp: number) => void;
  eventCategory: CATEGORIES;
  hideCopyButton?: boolean;
  isLatest?: boolean;
  renderMarkdown?: boolean;
  chartSpecs?: AskChatChartSpec[];
}> = ({
  text,
  citationMap,
  onClickTimestamp,
  eventCategory,
  hideCopyButton,
  isLatest,
  renderMarkdown = false,
  chartSpecs,
}) => {
  const sendGAEvent = useSendGAEvent();
  const toast = useToast();
  const isJson = text?.startsWith('{"notes":');
  const hasCharts = renderMarkdown && chartSpecs && chartSpecs.length > 0;

  return (
    <Flex flexDir="row" alignItems="flex-start" mt="24px" mr={2}>
      <Flex
        textColor="gray.800"
        fontSize="sm"
        whiteSpace={renderMarkdown ? "normal" : "pre-wrap"}
        p={2}
        borderRadius="md"
        bg={isLatest ? "purple.100" : "purple.50"}
        mr={2}
        flexDir="column"
        width="100%"
      >
        <Flex flexDir="row" width="100%">
          <Icon
            boxSize={6}
            color="purple.450"
            as={HiOutlineSparkles}
            p={1}
            flexShrink="0"
            mr={2}
          />
          {!isJson && text && !renderMarkdown && <Box pt="2px">{text}</Box>}
          {!isJson && text && renderMarkdown && (
            <Box pt="2px" width="100%" overflowX="auto">
              <PrettyMarkdown>{text}</PrettyMarkdown>
            </Box>
          )}
          {isJson && (
            <MessageContentWithCitations
              text={text || ""}
              citationMap={citationMap}
              onClickTimestamp={onClickTimestamp}
              eventCategory={eventCategory}
            />
          )}
          {!text && (
            <Box pt="4px">
              <Spinner color="purple.300" size="sm" />
            </Box>
          )}
        </Flex>
        {hasCharts &&
          chartSpecs?.map((spec, idx) => {
            const data = spec.labels.map((label, i) => ({
              name: label.length > 12 ? `${label.slice(0, 12)}…` : label,
              value: spec.values[i] ?? 0,
            }));
            const chartKey = `${spec.title ?? "chart"}-${spec.labels.join(
              "-"
            )}-${idx}`;
            return (
              <Box key={chartKey} mt={3} width="100%" minH="200px">
                {spec.title && (
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {spec.title}
                  </Text>
                )}
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={data}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="gray.200" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip />
                    <Bar
                      dataKey="value"
                      fill="purple.400"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            );
          })}
      </Flex>
      {!hideCopyButton && (
        <Box flexShrink="0" ml="auto">
          <Tooltip label="Copy to clipboard" placement="bottom-start">
            <IconButton
              aria-label="Copy response"
              icon={<CopyIcon fontSize="20px" />}
              variant="ghost"
              color="blue.600"
              onClick={() => {
                sendGAEvent("ask_copy_response", eventCategory);
                copy({
                  plain: botMessageToText(text),
                  html: botMessageToHtml(text, citationMap),
                });
                toast({
                  title: "Response copied to clipboard",
                  status: "success",
                });
              }}
            />
          </Tooltip>
        </Box>
      )}
    </Flex>
  );
};

/** All fields are optional because of partial type streaming */
type CitedNotes = {
  notes?: {
    note?: string;
    note_citations?: {
      call_id?: string;
      timestamp?: string;
    }[];
  }[];
};

// Parsing does not need to be robust since all fields are optional and checked on render
const parseToCitedNotes = (text: string | undefined): CitedNotes | null => {
  try {
    return JSON.parse(text || "");
  } catch (e) {
    return null;
  }
};

const botMessageToText = (text: string | undefined): string => {
  const notesMessage = parseToCitedNotes(text);
  if (notesMessage === null) {
    return text || "";
  }
  if (!notesMessage.notes) {
    return "";
  }
  return notesMessage.notes
    .map((note) => {
      return note.note;
    })
    .join("\n\n");
};

const botMessageToHtml = (
  text: string | undefined,
  citationMap: Map<string, number>
): string => {
  const notesMessage = parseToCitedNotes(text);
  if (notesMessage === null) {
    return text || "";
  }

  if (!text || text.trim() === "") {
    return "";
  }
  try {
    if (!notesMessage.notes) {
      return "";
    }
    return notesMessage.notes
      .map((note) => {
        if (!note.note) {
          return "";
        }
        const citationText = (note.note_citations || [])
          .map((citation) => {
            if (!citation.call_id || !citation.timestamp) {
              return "";
            }
            const citationKey = `${citation.call_id}-${citation.timestamp}`;
            const citationLabel =
              citationMap.get(citationKey)?.toString() || "";
            return `[<a href="https://app.brighthire.ai/interview/${citation.call_id}?t=${citation.timestamp}">${citationLabel}</a>]`;
          })
          .join(" ");
        return `${note.note} ${citationText}`;
      })
      .join("<br><br>");
  } catch (e) {
    return text;
  }
};

const MessageContentWithCitations: React.FC<{
  text: string;
  citationMap: Map<string, number>;
  onClickTimestamp?: (timestamp: number) => void;
  eventCategory: CATEGORIES;
}> = ({ text, citationMap, onClickTimestamp, eventCategory }) => {
  const sendGAEvent = useSendGAEvent();
  const [latestValidJsonText, setLatestValidJsonText] = useState<string | null>(
    null
  );
  useEffect(() => {
    try {
      JSON.parse(text);
      setLatestValidJsonText(text);
    } catch (e) {
      // Skip. Rarely a partial server message will not be parseable, but either the
      // previous or next message will be.
    }
  });
  if (latestValidJsonText === null) {
    return null;
  }
  const response = JSON.parse(latestValidJsonText);
  return (
    <Flex flexDir="column">
      {response.notes.map((note: any, noteIndex: number) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box pb="1rem" key={noteIndex}>
          {note.note}
          {(note.note_citations || []).map(
            (citation: any, citationIndex: number) => {
              const citationKey = `${citation.call_id}-${citation.timestamp}`;
              if (!citationMap.has(citationKey)) {
                citationMap.set(citationKey, citationMap.size + 1);
              }
              const citationNumber = citationMap.get(citationKey);

              // Clicks should use a non-reload navigate if the path is the same,
              // otherwise, open a new tab.
              const path = `/interview/${citation.call_id}`;
              const uri = `${path}?t=${citation.timestamp}`;
              return (
                <Link
                  // eslint-disable-next-line react/no-array-index-key
                  key={citationIndex}
                  fontWeight="normal"
                  href={uri}
                  target="_blank"
                  onClick={(e) => {
                    sendGAEvent("ask_click_citation", eventCategory);
                    if (path === window.location.pathname) {
                      onClickTimestamp?.(parseInt(citation.timestamp, 10));
                      e.preventDefault();
                    }
                  }}
                >
                  <MonospacedText
                    display="inline-block"
                    verticalAlign="super"
                    fontSize="0.6rem"
                    text={citationNumber?.toString() || ""}
                    marginRight="0.25rem"
                  />
                </Link>
              );
            }
          )}
        </Box>
      ))}
    </Flex>
  );
};

const UserMessage: React.FC<{
  text: string;
  isLatest?: boolean;
}> = ({ text, isLatest }) => {
  const currentUser = useCurrentUser();
  return (
    <Flex
      mt="24px"
      ml={10}
      mr={2}
      textColor="gray.800"
      fontSize="sm"
      whiteSpace="pre-wrap"
      p={2}
      pl={3}
      borderRadius="md"
      bg={isLatest ? "gray.100" : "gray.50"}
    >
      <Flex flexDir="row" width="100%">
        <Box pt="2px">{text}</Box>
        <Box pl={2} width={8} flexShrink="0" ml="auto">
          <Avatar user={currentUser} />
        </Box>
      </Flex>
    </Flex>
  );
};

const SuggestedMessage: React.FC<{
  message: string;
  onClick: (message: string) => void;
  eventCategory: CATEGORIES;
}> = ({ message: text, onClick, eventCategory }) => {
  const sendGAEvent = useSendGAEvent();
  return (
    <Box
      as="button"
      type="button"
      p="1px"
      borderRadius="md"
      background="linear-gradient(90deg, rgba(99, 102, 241, 0.4), rgba(168, 85, 247, 0.4))"
      onClick={() => {
        sendGAEvent("ask_click_suggested_question", eventCategory, text);
        onClick(text);
      }}
      _hover={{
        background:
          "linear-gradient(90deg, rgba(99, 102, 241, 0.6), rgba(168, 85, 247, 0.6))",
      }}
    >
      <Flex
        bg="white"
        borderRadius="md"
        px={3}
        py={1.5}
        fontSize="sm"
        fontWeight="medium"
        color="gray.900"
        alignItems="center"
        justifyContent="flex-start"
      >
        {text}
      </Flex>
    </Box>
  );
};
